/**
 * One checklist row (SPEC §8.1). Display mode renders text (links resolved to current
 * titles) with callouts/selection disabled so long-press drags cleanly; a tap switches to
 * a plain auto-sized <textarea> bound to the item's Y.Text, focused synchronously so the
 * iOS keyboard opens, with the caret where you tapped.
 */
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react'
import {
  describeRepeat,
  displayTitle,
  fromEditable,
  isSingleUrl,
  itemKind,
  parseTitle,
  relativeDayLabel,
  titleLinkIds,
  toEditable,
  formatTime,
  formatTimeShort,
  type ItemSnapshot,
} from '@terrazzo/core'
import { useItem, useRowSubtitle, useToday, useUsers } from '../store/hooks'
import { store } from '../store/store'
import { actions, isPlainRow } from '../store/actions'
import { isEditHeld, registerInput, startEditing, stopEditing, useEditing, gestureState } from '../store/ui'
import { useEditorOf } from '../store/presence'
import { navigate, itemPath, openSheet } from '../lib/router'
import { Checkbox } from './Checkbox'
import { IconChevronRight, IconGrip, IconMore, IconPaperclip, IconRepeat, IconLink } from './Icons'
import { NewMark } from '../lib/newMark'
import { scaled, uiScale } from '../lib/scale'
import { haptic } from '../lib/haptics'
import { dropIfEmptyNewCard } from './Card'
import { useSwipe } from './useSwipe'
import { LinkPicker, type LinkPickerHandle } from './LinkPicker'
import { unfurlInto } from '../lib/unfurl'
import { localTime } from '../lib/clock'
import { useArmed } from '../lib/checkGuard'

export interface RowNav {
  prev(id: string): string | null
  next(id: string): string | null
  /** Previous sibling in this list (indent target), if any. */
  prevSibling(id: string): string | null
  canOutdent(id: string): boolean
  hasOpenChildren(id: string): boolean
  /** Create a row after `id` (or as its first child) with a title; returns the new id. */
  addAfter(id: string, title: string, asFirstChild: boolean): string | null
  /** Create several rows after `id`; returns new ids. */
  addManyAfter(id: string, titles: string[]): string[]
}

export interface SortableBits {
  setNodeRef: (el: HTMLElement | null) => void
  listeners?: Record<string, unknown>
  attributes?: Record<string, unknown>
  style?: CSSProperties
  isDragging?: boolean
}

export interface RowProps {
  id: string
  depth: number
  scope: string
  nav: RowNav
  variant?: 'tree' | 'day' | 'flat'
  breadcrumb?: string
  hint?: string
  sortable?: SortableBits
  /** Preview depth while dragging (SortableTree projection). */
  dragDepth?: number
  indentWidth?: number
}

/** Ids of rows created empty by Enter in this session (removed again if left empty). */
export const ephemeral = new Set<string>()
let lastDragEnd = 0
export function markDragEnd() {
  lastDragEnd = Date.now()
}

function initialOf(name?: string) {
  return (name ?? '?').trim().charAt(0).toUpperCase() || '?'
}

function caretFromPoint(container: HTMLElement, x: number, y: number): number | null {
  const d = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null
    caretRangeFromPoint?: (x: number, y: number) => Range | null
  }
  let node: Node | null = null
  let offset = 0
  if (d.caretPositionFromPoint) {
    const p = d.caretPositionFromPoint(x, y)
    if (p) {
      node = p.offsetNode
      offset = p.offset
    }
  } else if (d.caretRangeFromPoint) {
    const r = d.caretRangeFromPoint(x, y)
    if (r) {
      node = r.startContainer
      offset = r.startOffset
    }
  }
  if (!node || !container.contains(node)) return null
  // Count editable characters before (node, offset). Link chips count as [[Title]].
  let count = 0
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
  let cur: Node | null
  while ((cur = walker.nextNode())) {
    const inLink = (cur.parentElement?.closest('[data-link]') ?? null) !== null
    if (cur === node) return count + offset + (inLink ? 2 : 0)
    count += (cur.textContent ?? '').length + (inLink ? 4 : 0)
  }
  return count
}

function autosize(el: HTMLTextAreaElement | null) {
  if (!el || CSS.supports?.('field-sizing', 'content')) return
  el.style.height = 'auto'
  el.style.height = `${el.scrollHeight}px`
}

const titleOf = (id: string) => store.get(id)?.title
/** How a linked item is labelled while editing: its display title, never empty (an empty `[[]]` wouldn't parse back). */
const labelOf = (id: string) => {
  const it = store.get(id)
  return it ? displayTitle(it.title, titleOf) || 'Untitled' : undefined
}
const resolveTitle = (label: string) => {
  const low = label.trim().toLowerCase()
  for (const it of store.idx.items.values()) {
    if (!store.idx.tree.inTrash.has(it.id) && displayTitle(it.title, titleOf).toLowerCase() === low) return it.id
  }
  return undefined
}

export const Row = memo(function Row(props: RowProps) {
  const { id, depth, scope, nav, variant = 'tree', sortable, dragDepth, indentWidth = 22 } = props
  const item = useItem(id)
  const edit = useEditing(scope, id)
  const editing = !!edit
  // On a phone, the first tap on its box makes it ready to check (lib/checkGuard.ts): it looks selected.
  const armKey = `${scope}:${id}`
  const armed = useArmed(armKey)
  const today = useToday()
  const users = useUsers()
  const editor = useEditorOf(id)
  const textRef = useRef<HTMLDivElement>(null)
  const taRef = useRef<HTMLTextAreaElement>(null)
  const pickerRef = useRef<LinkPickerHandle>(null)

  // ---- editing state -------------------------------------------------------
  const [text, setText] = useState('')
  const linkIds = useRef<string[]>([])
  const lastStored = useRef<string | null>(null)
  const pendingCaret = useRef<number | null>(null)
  const [picker, setPicker] = useState<{ query: string; start: number } | null>(null)

  useLayoutEffect(() => {
    if (!editing || !item) return
    const e = toEditable(item.title, labelOf)
    linkIds.current = e.ids
    lastStored.current = item.title
    setText(e.text)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, edit?.seq])

  // Remote edits while editing: rebuild the editable text and keep the caret stable.
  useLayoutEffect(() => {
    if (!editing || !item || item.title === lastStored.current) return
    const ta = taRef.current
    const e = toEditable(item.title, labelOf)
    const oldText = ta?.value ?? text
    const caret = ta?.selectionStart ?? oldText.length
    let prefix = 0
    while (prefix < oldText.length && prefix < e.text.length && oldText[prefix] === e.text[prefix]) prefix++
    const delta = e.text.length - oldText.length
    pendingCaret.current = caret > prefix ? Math.max(prefix, caret + delta) : caret
    linkIds.current = e.ids
    lastStored.current = item.title
    setText(e.text)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.title])

  useLayoutEffect(() => {
    autosize(taRef.current)
    if (pendingCaret.current !== null && taRef.current) {
      const p = pendingCaret.current
      pendingCaret.current = null
      taRef.current.setSelectionRange(p, p)
    }
  }, [text])

  const commit = (next: string, overrides?: Map<string, string>) => {
    const storage = fromEditable(next, linkIds.current, labelOf, (label) => overrides?.get(label) ?? resolveTitle(label))
    linkIds.current = titleLinkIds(storage)
    lastStored.current = storage
    if (storage !== item?.title) actions.setTitle(id, storage)
    if (next) ephemeral.delete(id)
  }

  const onChange = (next: string, caret: number) => {
    setText(next)
    commit(next)
    const before = next.slice(0, caret)
    const m = /\[\[([^\]\n]{0,40})$/.exec(before)
    setPicker(m ? { query: m[1]!, start: caret - m[0].length } : null)
  }

  const pickLink = (targetId: string) => {
    if (!picker) return
    const ta = taRef.current
    const caret = ta?.selectionStart ?? text.length
    const title =
      displayTitle(store.get(targetId)?.title ?? '', titleOf)
        .replace(/[[\]|]/g, ' ')
        .trim() || 'Untitled'
    const next = text.slice(0, picker.start) + `[[${title}]]` + text.slice(caret)
    const overrides = new Map([[title, targetId]])
    pendingCaret.current = picker.start + title.length + 4
    setPicker(null)
    setText(next)
    commit(next, overrides)
  }

  const exitIfEphemeral = () => {
    const current = store.get(id)
    // Only purge a row the user never put anything into (defaults like a Day's date are fine).
    const untouched = current && !current.title.trim() && !current.bodyText && !current.attachments.length && !current.link && !(store.tree.childrenOf.get(id)?.length ?? 0)
    if (ephemeral.has(id) && untouched) {
      ephemeral.delete(id)
      const parent = current.parentId
      actions.purge(id)
      // A brand-new card whose first task was abandoned too goes away with it.
      dropIfEmptyNewCard(parent)
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const ta = e.currentTarget
    if (picker && pickerRef.current?.handleKey(e)) return
    const caret = ta.selectionStart
    const caretEnd = ta.selectionEnd
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      if ((e.metaKey || e.ctrlKey) && item?.done !== null) {
        actions.toggleDone(id)
        return
      }
      // Enter on an empty indented row outdents it (checklist convention).
      if (!text && depth > 0 && nav.canOutdent(id)) {
        actions.outdent(id, { toast: false })
        startEditing(scope, id, 'end')
        return
      }
      const before = text.slice(0, caret)
      const after = text.slice(caretEnd)
      // Resolve links in the moved-down half while linkIds still covers the whole title.
      const afterStorage = after ? fromEditable(after, linkIds.current, labelOf, resolveTitle) : ''
      if (after) {
        setText(before)
        commit(before)
      }
      const asChild = !after && nav.hasOpenChildren(id)
      const newId = nav.addAfter(id, afterStorage, asChild)
      if (newId) {
        if (!afterStorage) ephemeral.add(newId)
        startEditing(scope, newId, 'start')
      }
      return
    }
    if (e.key === 'Backspace' && caret === 0 && caretEnd === 0) {
      const hasKids = (store.tree.childrenOf.get(id) ?? []).some((c) => !store.tree.inTrash.has(c))
      const prev = nav.prev(id)
      if (!text) {
        if (hasKids) return
        e.preventDefault()
        ephemeral.delete(id)
        actions.trashEmpty(id)
        if (prev) startEditing(scope, prev, 'end')
        else stopEditing(id)
        return
      }
      // Merge into the previous row — only when this row is just a title, so Backspace
      // never silently throws away notes, dates, files or tags.
      const prevItem = prev ? store.get(prev) : undefined
      if (prev && prevItem && item && !hasKids && prevItem.done !== true && isPlainRow(item)) {
        e.preventDefault()
        const prevEditable = toEditable(prevItem.title, labelOf).text
        ephemeral.delete(id)
        actions.mergeInto(prev, id)
        startEditing(scope, prev, prevEditable.length)
      }
      return
    }
    if (e.key === 'Tab') {
      e.preventDefault()
      if (e.shiftKey) {
        outdent()
      } else {
        const ps = nav.prevSibling(id)
        if (ps) actions.indent(id, ps, { toast: false })
      }
      startEditing(scope, id, caret)
      return
    }
    if (e.key === 'ArrowUp' && caret === 0 && caretEnd === 0) {
      const prev = nav.prev(id)
      if (prev) {
        e.preventDefault()
        startEditing(scope, prev, 'end')
      }
      return
    }
    if (e.key === 'ArrowDown' && caret === text.length) {
      const next = nav.next(id)
      if (next) {
        e.preventDefault()
        startEditing(scope, next, 'start')
      }
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      ta.blur()
    }
  }

  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const data = e.clipboardData.getData('text/plain')
    if (!data) return
    const ta = e.currentTarget
    if (data.includes('\n')) {
      const lines = data
        .split(/\r?\n/)
        .map((l) => l.replace(/^\s*(?:[-*•]\s+)?(?:\[[ xX]\]\s+)?/, '').trimEnd())
        .filter((l) => l.trim())
      if (lines.length > 1) {
        e.preventDefault()
        const caret = ta.selectionStart
        const first = text.slice(0, caret) + lines[0] + text.slice(ta.selectionEnd)
        setText(first)
        commit(first)
        const ids = nav.addManyAfter(id, lines.slice(1))
        const last = ids[ids.length - 1]
        if (last) startEditing(scope, last, 'end')
        return
      }
    }
    if (isSingleUrl(data) && !text.trim()) {
      e.preventDefault()
      const url = data.trim()
      setText(url)
      commit(url)
      void unfurlInto(id, url)
    }
  }

  // ---- swipe (touch) ---------------------------------------------------------
  // Swipes are instantly reversible, so they don't add Undo toasts. While editing, a swipe
  // still works if it starts off the text itself (gutter, row edges).
  // A task already at the top of its list turns into a heading when outdented once more.
  const topTask = variant === 'tree' && !!item && item.done !== null && !nav.canOutdent(id)
  const canOut = () => nav.canOutdent(id) || topTask
  const outdent = (opts: { toast?: boolean } = { toast: false }) => {
    if (nav.canOutdent(id)) actions.outdent(id, opts)
    else if (topTask) actions.makeHeading(id)
  }
  const swipe = useSwipe({
    enabled: variant === 'tree',
    ignore: (el) => editing && !!el.closest?.('textarea, .cb, button'),
    canIndent: () => !!nav.prevSibling(id),
    canOutdent: canOut,
    onIndent: () => {
      const ps = nav.prevSibling(id)
      if (ps) actions.indent(id, ps, { toast: false })
      if (editing) startEditing(scope, id, taRef.current?.selectionStart ?? 'end')
    },
    onOutdent: () => {
      outdent()
      if (editing) startEditing(scope, id, taRef.current?.selectionStart ?? 'end')
    },
  })

  // While editing, the pill's frame (not the text, checkbox or buttons) still drags the row.
  const listeners = sortable?.listeners as Record<string, (e: Event) => void> | undefined
  const dragListeners = useMemo(() => {
    if (!listeners || !editing) return listeners
    return Object.fromEntries(Object.entries(listeners).map(([k, fn]) => [k, (e: Event) => !(e.target as Element | null)?.closest?.('textarea, button, .cb, input') && fn(e)]))
  }, [listeners, editing])

  useEffect(() => {
    if (swipe.intent) haptic(6)
  }, [swipe.intent])

  const onTextClick = (e: React.MouseEvent) => {
    if (swipe.consumedTap() || gestureState.get() !== 'none' || Date.now() - lastDragEnd < 300) return
    const target = e.target as HTMLElement
    const link = target.closest('[data-link]') as HTMLElement | null
    if (link?.dataset.link) {
      e.preventDefault()
      navigate(itemPath(link.dataset.link))
      return
    }
    if (target.closest('a[href]')) return
    const caret = textRef.current ? caretFromPoint(textRef.current, e.clientX, e.clientY) : null
    startEditing(scope, id, caret ?? 'end')
  }

  useEffect(() => {
    if (!editing) setPicker(null)
  }, [editing])

  const segments = useMemo(() => (item ? parseTitle(item.title, (x) => displayTitle(store.get(x)?.title ?? '', titleOf) || 'Untitled') : []), [item])

  // The card's chosen fields (money by default), with totals on parents.
  // The subtitle (the line under the title), as the card — or this tab — sets it.
  const subtitle = useRowSubtitle(id)
  const fieldBits = subtitle.bits
  if (!item) return null
  const kind = itemKind(item)
  const hasChildren = (store.tree.childrenOf.get(id) ?? []).some((c) => !store.tree.inTrash.has(c) && !store.tree.inArchive.has(c))
  const hasDetails = hasChildren || !!item.bodyText || item.attachments.length > 0 || Object.keys(item.props).some((k) => k !== 'view')
  // Swipe preview: past the commit point the row slides into the depth it will land at,
  // and keeps following the finger from there; letting go keeps it, backing off cancels.
  const headingPreview = swipe.intent === 'outdent' && topTask
  const swipeShift = swipe.intent === 'indent' ? 1 : swipe.intent === 'outdent' && !headingPreview ? -1 : 0
  const shownDepth = dragDepth ?? depth + swipeShift
  const swipeX = swipe.dx - swipeShift * indentWidth * uiScale()
  const assignee = item.assignee ? users.get(item.assignee) : undefined
  const overdue = kind === 'task' && item.done === false && !!item.date && item.date < today
  const linkPreview = item.link && item.link.url && (item.title.trim() === item.link.url || !item.title.trim()) ? item.link : null

  const style: CSSProperties = {
    paddingLeft: scaled(shownDepth * indentWidth),
    ['--pad' as string]: scaled(shownDepth * indentWidth),
    transform: swipeX ? `translateX(${swipeX}px)` : undefined,
    transition: swipe.dx ? 'padding-left 140ms ease-out' : 'transform 160ms ease-out, padding-left 140ms ease-out',
    ...sortable?.style,
  }

  return (
    <div
      ref={sortable?.setNodeRef}
      className={`row group ${editing ? 'editing' : ''} ${armed ? 'armed' : ''} ${sortable?.isDragging ? 'dragging-source' : ''}`}
      style={style}
      data-row-id={id}
      data-depth={shownDepth}
      {...(sortable?.attributes as object)}
      {...(dragListeners as object)}
      {...swipe.handlers}
      tabIndex={-1}
    >
      {/* A group (a top-level row with rows under it): a soft strip across the width (SPEC §7.10). */}
      {variant === 'tree' && depth === 0 && hasChildren && <span className="row-strip" aria-hidden="true" />}
      {swipe.intent === 'indent' && (
        // "└" connector: tucked under the row above.
        <span
          aria-hidden="true"
          className="pointer-events-none absolute top-0 h-[0.9375rem] w-2.5 rounded-bl-md border-b-[1.5px] border-l-[1.5px] border-fg/35"
          style={{ left: scaled(shownDepth * indentWidth - 12) }}
        />
      )}
      {/* On Days: the time down the left in mono (SPEC §7.10). */}
      {variant === 'day' && <span className="lab mt-[0.5rem] w-11 flex-none text-muted">{item.time ? formatTimeShort(localTime(item.date, item.time)) : kind === 'label' ? 'All day' : ''}</span>}
      {kind === 'task' && !headingPreview ? (
        <Checkbox checked={item.done === true} onToggle={() => actions.toggleDone(id)} label={displayTitle(item.title, titleOf)} recurring={!!item.recur} guard={armKey} ready={editing} />
      ) : kind === 'label' && variant === 'day' ? (
        <span className="w-[1.875rem] flex-none" aria-hidden="true" />
      ) : (
        // No checkbox: text sits flush with the checkbox edge so these rows work as headings.
        <span className="w-[0.1875rem] flex-none" aria-hidden="true" />
      )}

      <div className="relative min-w-0 flex-1">
        {editing ? (
          <>
            <textarea
              ref={(el) => {
                taRef.current = el
                registerInput(scope, id, el)
              }}
              className="row-input"
              rows={1}
              value={text}
              placeholder="New item"
              enterKeyHint="next"
              autoCapitalize="sentences"
              spellCheck
              onChange={(e) => onChange(e.target.value, e.target.selectionStart)}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              onBlur={(e) => {
                // Being dragged while edited: focus is parked, the pill stays.
                if (isEditHeld(id)) return
                const next = e.relatedTarget as HTMLElement | null
                if (next?.closest?.(`[data-row-id="${id}"]`)) return
                setPicker(null)
                exitIfEphemeral()
                stopEditing(id)
              }}
            />
            {picker && <LinkPicker ref={pickerRef} query={picker.query} excludeId={id} anchor={taRef} onPick={pickLink} onClose={() => setPicker(null)} />}
          </>
        ) : (
          <div ref={textRef} className={`row-text cursor-text ${item.done === true ? 'text-muted line-through decoration-faint' : ''}`} data-placeholder="New item" onClick={onTextClick}>
            {linkPreview ? (
              <span className="inline-flex max-w-full items-center gap-1.5 align-top">
                <IconLink size={14} className="flex-none text-muted" />
                <a href={linkPreview.url} target="_blank" rel="noopener noreferrer" className="truncate underline decoration-line-2 underline-offset-2" onClick={(e) => e.stopPropagation()}>
                  {linkPreview.title || linkPreview.url}
                </a>
                {linkPreview.siteName && <span className="flex-none text-xs text-faint">{linkPreview.siteName}</span>}
              </span>
            ) : (
              segments.map((s, i) =>
                s.kind === 'link' ? (
                  <span key={i} className="wikilink" data-link={s.id}>
                    {s.text}
                  </span>
                ) : s.kind === 'url' ? (
                  <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="underline decoration-line-2 underline-offset-2" onClick={(e) => e.stopPropagation()}>
                    {s.text.replace(/^https?:\/\/(www\.)?/, '').slice(0, 48)}
                  </a>
                ) : (
                  <span key={i}>{s.text}</span>
                ),
              )
            )}
            <NewMark id={id} />
          </div>
        )}
        {linkPreview?.image && variant !== 'day' && (
          <img src={linkPreview.image} alt="" loading="lazy" className="mb-1.5 ml-0.5 h-16 w-28 rounded-md object-cover" onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />
        )}
        {/* The date/time line stays put while the title is being edited. (A note shows as the › on the right, not here.) */}
        {(props.hint ||
          props.breadcrumb ||
          (subtitle.date && item.date && variant !== 'day') ||
          (subtitle.repeat && item.recur) ||
          (subtitle.person && assignee) ||
          item.attachments.length > 0 ||
          fieldBits.length > 0) && (
          // The details: a mono line under the text (SPEC §7.10).
          <div className="lab -mt-1 flex flex-wrap items-center gap-x-2 pb-1 pl-0.5 text-muted">
            {props.breadcrumb && <span className="max-w-full truncate">{props.breadcrumb}</span>}
            {props.hint && <span className="text-danger">{props.hint}</span>}
            {subtitle.date && item.date && variant !== 'day' && (
              <span className={overdue ? 'text-danger' : ''}>
                {/* Done for this time: when it comes back. */}
                {item.reopensOn && item.done === true ? `Back ${relativeDayLabel(item.reopensOn, today)}` : relativeDayLabel(item.date, today)}
                {item.time && !item.reopensOn ? ` · ${formatTime(localTime(item.date, item.time))}` : ''}
              </span>
            )}
            {subtitle.repeat && item.recur && (
              <span className="inline-flex items-center gap-0.5" title={describeRepeat(item.recur, item.date)}>
                <IconRepeat size={11} />
              </span>
            )}
            {item.attachments.length > 0 && (
              <span className="inline-flex items-center gap-0.5">
                <IconPaperclip size={11} />
                {item.attachments.length}
              </span>
            )}
            {fieldBits.map((b) => (
              <span key={b} className="tabular-nums">
                {b}
              </span>
            ))}
            {subtitle.person && assignee && <span title={assignee.name}>{assignee.name.split(' ')[0]}</span>}
          </div>
        )}
      </div>

      {editor && (
        <span className="mt-2 mr-1 inline-flex h-3.5 w-3.5 flex-none items-center justify-center rounded-full bg-focus text-[0.5rem] font-bold text-white" title={`${editor.name} is editing`}>
          {initialOf(editor.name)}
        </span>
      )}
      <div className="flex flex-none items-start" data-row-tools={id}>
        {editing && variant === 'tree' && (
          // The pill's handle while editing: long-press/drag to move, swipe to re-nest.
          <span
            data-grip=""
            aria-hidden="true"
            title="Drag to move · slide to indent"
            className="-my-[0.4375rem] flex h-11 w-7 cursor-grab touch-none items-center justify-center text-faint active:cursor-grabbing"
          >
            <IconGrip size={16} />
          </span>
        )}
        {editing && (
          <button
            type="button"
            className="-my-[0.1875rem] flex h-9 w-8 items-center justify-center text-muted hover:text-fg"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              ephemeral.delete(id)
              openSheet('row-menu', { id })
            }}
            aria-label="Item options"
          >
            <IconMore size={18} />
          </button>
        )}
        {(hasDetails || editing) && (
          <button
            type="button"
            className="group/open -my-[0.1875rem] -mr-1 flex h-9 w-9 items-center justify-center"
            // Keep the textarea focused via mousedown (preventing pointerdown drops the tap in WebKit).
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => editing && e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation()
              ephemeral.delete(id)
              if (editing) stopEditing(id)
              navigate(itemPath(id))
            }}
            aria-label="Open"
          >
            {/* Just the › at rest (it means there's something inside); a paper square on the row you're editing. */}
            <span className={`open-box transition-colors group-hover/open:text-fg ${editing ? '' : 'text-faint'}`}>
              <IconChevronRight size={15} />
            </span>
          </button>
        )}
      </div>
    </div>
  )
})

export function rowSnapshot(id: string): ItemSnapshot | undefined {
  return store.get(id)
}
